import type { IntlShape } from 'react-intl';

import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';

import type { TicketPermissionType } from '~/enums/TicketPermissionType';

import {
  getStageConfig,
  TICKET_NAMING_CONVENTION_ALLOWED_TYPES,
} from '~/common/constants/ticket';
import { TicketStageType } from '~/enums/TicketStageType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { WorkflowCreatorPermissionType } from '~/enums/WorkflowCreatorPermissionType';

interface StageEntry {
  order?: number;
  value?: string;
}

interface DocumentVersionLike {
  versionNumber?: number;
  fileType?: string;
  name?: string | null;
  hasWorkingDraft?: boolean;
  isCounterparty?: boolean;
}

interface DocumentLike {
  name?: string | null;
  version?: number;
}

interface NamingConventionEntry {
  type: string;
  value: string;
}

export function getStatusTextStatus(phaseStatus?: string | null) {
  switch (phaseStatus) {
    case TicketStatusType.Approved:
      return 'success';
    case TicketStatusType.Rejected:
      return 'danger';
    case TicketStatusType.Pending:
    default:
      return 'warn';
  }
}

export function testIsUnassignedUser(
  approvalItem: { externalUser?: unknown; userId?: string | number | null },
  users: Record<string | number, unknown>,
) {
  const hasExternalUser = approvalItem.externalUser;
  const hasApprover = approvalItem.userId;
  const hasNoApprover = !hasExternalUser && !hasApprover;
  const isRemovedUser = !!hasApprover && !users[hasApprover];
  return hasNoApprover || isRemovedUser;
}

export function testIsCounterpartySigner(
  judgment?: { isInternal?: boolean } | null,
) {
  return judgment && !judgment?.isInternal;
}

/**
 * Returns true if the approval item is from a CC Signer.
 * @param approvalItem
 */
export function testIsCcSigner(approvalItem: {
  status?: string;
  ccRecipients?: Record<string, unknown> | null;
}) {
  return (
    approvalItem.status === TicketStatusType.NotApplicable ||
    !!(
      approvalItem.ccRecipients &&
      Object.keys(approvalItem.ccRecipients).length > 0
    )
  );
}

/** A valid signer post-revamp needs only an email at a minimum */
export function testHasInvalidSignersEsignRevamp(
  signerRecipients: { external?: { email?: string | null } | null }[],
) {
  return signerRecipients.some((recipient) => !recipient.external?.email);
}

export const getEnabledStages = (
  ticket: { stages: Record<string, { isEnabled?: boolean }> },
  intl: IntlShape,
) => {
  const enabledStages = Object.entries(ticket.stages).reduce(
    (acc, [value, stage]) => {
      const { isEnabled } = stage;
      const { label, order } =
        getStageConfig(intl)[value as keyof ReturnType<typeof getStageConfig>];
      if (isEnabled) {
        acc.push({
          label,
          order,
          value,
        });
      }
      return acc;
    },
    [] as { label: string; order: number; value: string }[],
  );

  return sortBy(enabledStages, ['order'], ['asc']);
};

export const getSimplifiedStageNames = (
  ticket: { enabledStageNames: string[] },
  intl: IntlShape,
) => {
  const { enabledStageNames } = ticket;
  const enabledStages = enabledStageNames.map((stageName) => {
    const { label, order } =
      getStageConfig(intl)[
        stageName as keyof ReturnType<typeof getStageConfig>
      ];
    return { label, order, value: stageName };
  });
  return sortBy(enabledStages, ['order'], ['asc']);
};

/**
 * Returns the WorkflowCreatorPermissionType for relating TicketStageType.  Returns null if unmapped.
 * @param stage
 */
export const mapTicketStageToWorkflowCreatorPermission = (
  stage?: string | null,
) => {
  switch (stage) {
    case TicketStageType.Edit:
      return WorkflowCreatorPermissionType.Edit;
    case TicketStageType.Review:
      return WorkflowCreatorPermissionType.Review;
    case TicketStageType.Sign:
      return WorkflowCreatorPermissionType.Sign;
    case TicketStageType.Finalize:
      return WorkflowCreatorPermissionType.Finalize;
    case TicketStageType.Ticket:
      return WorkflowCreatorPermissionType.Ticket;
    default:
      return null;
  }
};

/**
 * Returns the permissions associated with a ticket stage.
 * @param permissionMap
 * @param stage
 * @returns Array of TicketPermissionType
 */
export const getStagePermissions = <T extends string = TicketPermissionType>(
  permissionMap: Partial<Record<string, T[]>>,
  stage?: string | null,
): T[] => {
  const permissionType = mapTicketStageToWorkflowCreatorPermission(stage);
  return permissionMap[String(permissionType)] || [];
};

/**
 * @param stage The current stage
 * @param stages Array of stages
 * @returns The next stage
 */
export const getNextStageType = (
  stage: string | null | undefined,
  stages: ReadonlyArray<StageEntry | string>,
) => {
  const sortedStages = orderBy(stages, ['order']);
  const index = sortedStages.findIndex(
    (entry) => (typeof entry === 'string' ? undefined : entry.value) === stage,
  );
  const nextStage = sortedStages[index + 1];

  return index < 0 || typeof nextStage === 'string'
    ? null
    : (nextStage?.value ?? null);
};

// Checks if the user should be prompted to fill in signer information in the intake form (post esign revamp)
export function shouldFillFormQuestionsInSignStageEsignatureRevamp(
  ticket: { stage?: string; hasUnpopulatedSignerField?: boolean },
  envelopeStatus?: string | null,
) {
  return (
    ticket.stage === TicketStageType.Sign &&
    ticket.hasUnpopulatedSignerField &&
    envelopeStatus !== TicketStatusType.Signed
  );
}

/**
 * Returns the file extension of the ticket version
 * @param ticket current ticket object from the state
 * @returns the file extension with the dot, such as '.pdf', '.docx', etc.
 */
export function getTicketDocumentFileExtension(ticket: {
  document: DocumentLike;
  documentVersions: DocumentVersionLike[];
}) {
  const { document, documentVersions } = ticket;
  const fileType = documentVersions.find(
    (v) => v.versionNumber === document.version,
  )?.fileType;
  return fileType ? '.' + fileType : fileType;
}

/**
 * Returns false if the latest ticket version has a working draft
 * @param documentVersions versions of the ticket document, latest first
 */
export function latestVersionHasWorkingDraft(
  documentVersions: DocumentVersionLike[],
) {
  return documentVersions[0]?.hasWorkingDraft === true;
}

/**
 * Return versionNumber's version is a counterparty from upload version
 */
export function isCurrentVersionCounterparty(
  documentVersions: DocumentVersionLike[],
  versionNumber?: number,
) {
  const version = documentVersions.find(
    (v) => v.versionNumber === versionNumber,
  );
  return version?.isCounterparty === true;
}

export function getVersionFileName(
  document?: DocumentLike | null,
  version?: DocumentVersionLike | null,
) {
  if (!document || !version) return '';
  const name = version.name ?? document.name;
  if (name) {
    const fileExt = version.fileType;
    if (!fileExt) return name;
    return name.endsWith(fileExt) ? name : `${name}.${fileExt}`;
  }

  return '';
}

export const testValidFieldsInTicketNamingConvention = (
  ticketNamingConvention: NamingConventionEntry[],
  fields: Record<string, { type: string } | undefined>,
) => {
  return ticketNamingConvention
    .filter((entry) => entry.type === 'field')
    .every((entry) => {
      const field = fields[entry.value];
      return (
        !!field && TICKET_NAMING_CONVENTION_ALLOWED_TYPES.includes(field.type)
      );
    });
};

export const testValidFieldsForArchiveSubFolder = (
  paths: NamingConventionEntry[],
  fields: Record<string, unknown>,
) => {
  return paths
    .filter((entry) => entry.type === 'field')
    .every((entry) => fields[entry.value]);
};
